import React, { useState, useEffect } from 'react';
import { Form, Input, Typography, Button, Card, Row, Col, Divider, Select } from 'antd';
import { PlusOutlined, DeleteOutlined } from '@ant-design/icons';

const { Text } = Typography;
const { TextArea } = Input;
const { Option } = Select;

const SwitchCaseNodeProperties = ({ config, form, inputSchema }) => {
  const [cases, setCases] = useState(config.cases || []);

  // Update form when cases change
  useEffect(() => {
    form.setFieldsValue({
      cases: cases
    });
  }, [cases, form]);

  // Add new case
  const addCase = () => {
    const newCase = {
      id: `case_${Date.now()}`,
      condition: '',
      label: `Case ${cases.length + 1}`,
      description: ''
    };
    setCases([...cases, newCase]);
  };

  // Remove case
  const removeCase = (index) => {
    const newCases = cases.filter((_, i) => i !== index);
    setCases(newCases);
  };

  // Update case
  const updateCase = (index, updates) => {
    const newCases = [...cases];
    newCases[index] = { ...newCases[index], ...updates };
    setCases(newCases);
  };

  return (
    <>
      <Form.Item label="Switch Variable/Expression" name="switchExpression" initialValue={config.switchExpression || ''} rules={[{ required: true }]}>
        {inputSchema && inputSchema.length > 0 ? (
          <Select placeholder="Select column or enter expression" showSearch allowClear mode="tags" maxTagCount={1}>
             {inputSchema.map(col => (
                <Option key={col.name} value={col.name}>{col.name}</Option>
              ))}
          </Select>
        ) : (
          <Input placeholder="e.g., row.status or row.amount" />
        )}
      </Form.Item>

      <Divider orientation="left" style={{ fontSize: '12px' }}>Case Conditions</Divider>
      
      {/* Hidden form field to store cases in form */}
      <Form.Item name="cases" initialValue={cases} style={{ display: 'none' }}>
        <Input />
      </Form.Item>

      {/* Cases List */}
      <div style={{ marginBottom: 16 }}>
        {cases.map((caseItem, index) => (
          <Card 
            key={caseItem.id}
            size="small" 
            style={{ marginBottom: 12 }}
            title={
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Text strong>{caseItem.label || `Case ${index + 1}`}</Text>
                <Button 
                  type="text" 
                  danger
                  size="small"
                  icon={<DeleteOutlined />}
                  onClick={() => removeCase(index)}
                />
              </div>
            }
          >
            <Row gutter={12}>
              <Col span={12}>
                <div style={{ marginBottom: 8 }}>
                  <Text style={{ fontSize: '12px', color: '#666' }}>Case Label</Text>
                  <Input 
                    size="small"
                    value={caseItem.label}
                    onChange={(e) => updateCase(index, { label: e.target.value })}
                    placeholder="Case name"
                  />
                </div>
              </Col>
              <Col span={12}>
                <div style={{ marginBottom: 8 }}>
                  <Text style={{ fontSize: '12px', color: '#666' }}>Condition</Text>
                  <Input 
                    size="small"
                    value={caseItem.condition}
                    onChange={(e) => updateCase(index, { condition: e.target.value })}
                    placeholder="e.g., > 100 or === 'active'"
                  />
                </div>
              </Col>
            </Row>

            <div style={{ marginBottom: 8 }}>
              <Text style={{ fontSize: '12px', color: '#666' }}>Description</Text>
              <Input 
                size="small"
                value={caseItem.description}
                onChange={(e) => updateCase(index, { description: e.target.value })}
                placeholder="Describe when this case applies"
              />
            </div>
          </Card>
        ))}
      </div>

      {/* Add New Case Button */}
      <Button 
        type="dashed" 
        onClick={addCase}
        block
        icon={<PlusOutlined />}
        style={{ marginBottom: 16 }}
      >
        Add Case
      </Button>

      <Form.Item label="Default Case Behavior" name="defaultCase" initialValue={config.defaultCase || 'pass_through'}>
        <Input placeholder="What happens when no case matches (e.g., 'pass_through', 'stop', 'error')" />
      </Form.Item>

      <div style={{ padding: '8px 12px', backgroundColor: '#f9f9f9', borderRadius: '4px', fontSize: '12px' }}>
        <Text type="secondary">
          <strong>Switch/Case Node:</strong><br/>
          • Routes data based on multiple conditions<br/>
          • Each case creates a separate output path<br/>
          • Switch expression is evaluated against case conditions<br/>
          • Default case handles unmatched data
        </Text>
      </div>
    </>
  );
};

export default SwitchCaseNodeProperties;